import { ChangeDetectorRef, Component, EventEmitter, Input, OnInit, Output } from "@angular/core";
import { TranslocoService } from "@jsverse/transloco";
import { ConfirmationService } from "primeng/api";
import { DialogService } from "primeng/dynamicdialog";
import { take } from "rxjs";
import { ContactInputModel } from "@premise-hazard-models/contact-input.model";
import { PremiseHazard } from "@premise-hazard-models/premise-hazard.model";
import { TemplateConstants } from "@constants/template-constants";
import { ContactFormDialogComponent, ContactFormDialogData } from "./contact-form-dialog/contact-form-dialog.component";

@Component({
  selector: 'app-premise-hazard-item-contacts',
  templateUrl: './premise-hazard-item-contacts.component.html',
  styleUrls: ['./premise-hazard-item-contacts.component.scss'],
  standalone: false,
})
export class PremiseHazardItemContactsComponent implements OnInit {
  @Input() SelectedPremiseHazard?: PremiseHazard;
  @Input() IsUpdating: boolean = false;

  @Output() contactsChanged = new EventEmitter<ContactInputModel[]>();

  public ContactsToAdd: ContactInputModel[] = [];
  public DEVICE_TYPES = TemplateConstants.DeviceTypes;

  private removeDeviceModalTitle!: string;
  private removeDeviceModalMessage!: string;
  private removeContactModalTitle!: string;
  private removeContactModalMessage!: string;

  constructor(
    private readonly confirmationService: ConfirmationService,
    private readonly dialogService: DialogService,
    private readonly cdr: ChangeDetectorRef,
    private readonly transloco: TranslocoService) {
  }

  ngOnInit(): void {
    this.loadTranslations();
    this.initializeContacts();
  }

  private loadTranslations(): void {
    this.transloco.selectTranslate('PremiseHazard.Item.modal.removeDevice.header').pipe(take(1)).subscribe(res => this.removeDeviceModalTitle = res);
    this.transloco.selectTranslate('PremiseHazard.Item.modal.removeDevice.content').pipe(take(1)).subscribe(res => this.removeDeviceModalMessage = res);
    this.transloco.selectTranslate('PremiseHazard.Item.modal.removeContact.header').pipe(take(1)).subscribe(res => this.removeContactModalTitle = res);
    this.transloco.selectTranslate('PremiseHazard.Item.modal.removeContact.content').pipe(take(1)).subscribe(res => this.removeContactModalMessage = res);
  }

  private initializeContacts(): void {
    if (this.IsUpdating && this.SelectedPremiseHazard!.contacts) {
      this.ContactsToAdd = this.SelectedPremiseHazard!.contacts.map(contact => {
        return {
          name: contact.name,
          id: contact.id,
          devices: contact.devices?.map(device => {
            return {
              id: device.id,
              address: device.address,
              deviceType: device.deviceType,
            };
          }) || []
        };
      });
      this.emitContactsChanged();
    }
  }

  public openContactModal(): void {
    const ref = this.dialogService.open(ContactFormDialogComponent, {
      header: this.transloco.translate('PremiseHazard.Item.modal.contact.header'),
      modal: true,
      closable: false,
      dismissableMask: false,
      closeOnEscape: false,
      width: '600px',
    });
    ref.onClose.pipe(take(1)).subscribe((result: ContactInputModel | null) => {
      if (!result) {
        return;
      }
      this.ContactsToAdd.push(result);
      this.emitContactsChanged();
      this.cdr.detectChanges();
    });
  }

  public removeContact(index: number): void {
    if (index < 0 || index >= this.ContactsToAdd.length) {
      return;
    }
    this.confirmRemove(this.removeContactModalTitle, this.removeContactModalMessage, () => {
      this.ContactsToAdd.splice(index, 1);
      this.emitContactsChanged();
      this.cdr.detectChanges();
    });
  }

  public removeDeviceFromContact(contactIdx: number, deviceIdx: number): void {
    if (contactIdx < 0 || contactIdx >= this.ContactsToAdd.length) {
      return;
    }
    const contact = this.ContactsToAdd[contactIdx];

    if (deviceIdx < 0 || deviceIdx >= contact.devices.length) {
      return;
    }

    this.confirmRemove(this.removeDeviceModalTitle, this.removeDeviceModalMessage, () => {
      this.ContactsToAdd[contactIdx].devices.splice(deviceIdx, 1);
      this.emitContactsChanged();
      this.cdr.detectChanges();
    });
  }

  private confirmRemove(header: string, message: string, accept: () => void): void {
    this.confirmationService.confirm({
      header,
      message,
      acceptIcon: 'ic_trash',
      rejectIcon: 'ic_remove',
      acceptLabel: this.transloco.translate('Buttons.remove'),
      rejectLabel: this.transloco.translate('Buttons.cancel'),
      accept,
    });
  }

  public trackByContactIndex(index: number, item: any): number {
    return index;
  }

  public getContacts(): ContactInputModel[] {
    return this.ContactsToAdd;
  }

  private emitContactsChanged(): void {
    this.contactsChanged.emit(this.ContactsToAdd);
  }
}
